import defaultTheme from 'tailwindcss/defaultTheme.js';
import { utopiaClamp } from './assets/scripts/utils/utopiaClamp.js';

const spacings = {
    'gutter': '20px',
    'gutterMobile': '16px',
    'containerMargin': '32px',
    'containerMarginMobile': '16px',
    'fluid-xs': utopiaClamp(8, 10),
    'fluid-sm': utopiaClamp(16, 24),
    'fluid-md': utopiaClamp(24, 32),
    'fluid-lg': utopiaClamp(32, 40),
    'fluid-xl': utopiaClamp(40, 56),
    'fluid-2xl': utopiaClamp(56, 80),
    'fluid-3xl': utopiaClamp(80, 128),
    'fluid-4xl': utopiaClamp(128, 160)
};

/** @type {import('tailwindcss').Config} */
export default {
    content: ['./views/**/*.twig', './assets/scripts/**/*'],
    prefix: 'u-',
    corePlugins: {
        container: false
    },
    theme: {
        extend: {
            fontFamily: {
                sans: ['Sequel Sans Medium Disp', ...defaultTheme.fontFamily.sans],
                'sans-medium': ['Sequel Sans Medium Body', ...defaultTheme.fontFamily.sans],
                'sans-book': ['Sequel Sans Book Body', ...defaultTheme.fontFamily.sans],
                mono: ['Maison Mono', ...defaultTheme.fontFamily.mono]
            },
            fontSize: {
                'display-1': [
                    utopiaClamp(56, 188),
                    {
                        lineHeight: '.85',
                        fontWeight: '415',
                        letterSpacing: '-.03em'
                    }
                ],
                'display-2': [
                    utopiaClamp(56, 144),
                    {
                        lineHeight: '.85',
                        fontWeight: '415',
                        letterSpacing: '-.03em'
                    }
                ],
                'display-3': [
                    utopiaClamp(56, 110),
                    {
                        lineHeight: '.85',
                        fontWeight: '415',
                        letterSpacing: '-.03em'
                    }
                ],
                h1: [
                    utopiaClamp(48, 84),
                    {
                        lineHeight: '1',
                        fontWeight: '415',
                        letterSpacing: '-.03em'
                    }
                ],
                h2: [
                    utopiaClamp(42, 64),
                    {
                        lineHeight: '1',
                        fontWeight: '415',
                        letterSpacing: '-.03em'
                    }
                ],
                h3: [
                    utopiaClamp(34, 49),
                    {
                        lineHeight: '1.2',
                        fontWeight: '415',
                        letterSpacing: '-.03em'
                    }
                ],
                h4: [
                    utopiaClamp(28, 37),
                    {
                        lineHeight: '1',
                        fontWeight: '415',
                        letterSpacing: '-.02em'
                    }
                ],
                h5: [
                    utopiaClamp(26, 29),
                    {
                        lineHeight: '1.1',
                        fontWeight: '415',
                        letterSpacing: '-.01em'
                    }
                ],
                h6: [
                    utopiaClamp(18, 22),
                    {
                        lineHeight: '1.2',
                        fontWeight: '415'
                    }
                ],
                body: [
                    utopiaClamp(17, 17),
                    {
                        lineHeight: '1.2',
                        fontWeight: '415'
                    }
                ],
                'body-large': [
                    utopiaClamp(18, 22),
                    {
                        lineHeight: '1.2',
                        fontWeight: '415'
                    }
                ],
                'body-medium': [
                    utopiaClamp(18, 18),
                    {
                        lineHeight: '1.6',
                        fontWeight: '315'
                    }
                ],
                'label-medium': [
                    utopiaClamp(16, 16),
                    {
                        lineHeight: '1.3',
                        fontWeight: '400'
                    }
                ],
                label: [
                    utopiaClamp(14, 14),
                    {
                        lineHeight: '1.3',
                        fontWeight: '400'
                    }
                ],
                'label-small': [
                    utopiaClamp(12, 12),
                    {
                        lineHeight: '1.3',
                        fontWeight: '400'
                    }
                ],
                'label-tiny': [
                    utopiaClamp(10, 10),
                    {
                        lineHeight: '1.3',
                        fontWeight: '400'
                    }
                ],
                'none': [0]
            },
            colors: {
                white: '#ffffff',
                black: '#222223',
                cream: '#F0EEEB',
                jade: '#20A556',
                jadeDark: '#13733C',
                sapphire: '#3E64CF',
                wine: '#672B3C',
                lavender: '#B5B7D8',
                pistachio: '#D5E7B5',
                coral: '#E25856'
            },
            screens: {
                'to-2xs': { max: '339px' },
                '2xs': '340px',
                'to-xs': { max: '579px' },
                xs: '580px',
                'to-sm': { max: '699px' },
                sm: '700px',
                'to-md': { max: '999px' },
                md: '1000px',
                'to-lg': { max: '1199px' },
                lg: '1200px',
                'to-xl': { max: '1399px' },
                xl: '1400px',
                'to-2xl': { max: '1599px' },
                'figma': '1440px',
                'to-figma': { max: '1439px' },
                '2xl': '1600px',
                'to-3xl': { max: '1799px' },
                '3xl': '1800px',
                'to-4xl': { max: '1999px' },
                '4xl': '2000px',
                'to-5xl': { max: '2399px' },
                '5xl': '2400px'
            },
            spacing: {
                ...spacings
            },
            gap: {
                ...spacings
            },
            transitionDuration: {
                fast: '0.2s',
                default: '0.4s',
                slow: '0.6s',
                slower: '0.8s',
                slowest: '1s'
            },
            transitionTimingFunction: {
                default: 'linear(0, 0.012 0.9%, 0.049 2%, 0.409 9.3%, 0.513 11.9%, 0.606 14.7%, 0.691 17.9%, 0.762 21.3%, 0.82 25%, 0.868 29.1%, 0.907 33.6%, 0.937 38.7%, 0.976 51.3%, 0.994 68.8%, 1)'
            },
            zIndex: {
                modal: '200',
                sticky: '150',
                header: '100',
                above: '1',
                default: '0',
                below: '-1'
            },
            borderWidth: {
                1: '1px'
            },
            maxWidth: {
                'edito': '680px',
            },
        }
    },
    plugins: []
};
